import { useSyncExternalStore } from 'react'

// ─── Toast store ───────────────────────────────────────────────────────────
//
// A dependency-free, module-level toast store. The command centre had no toast
// primitive and the locked stack (CLAUDE.md) discourages adding UI deps, so this
// is a tiny pub/sub the <Toaster /> subscribes to via useSyncExternalStore.
// `toast()` can be called from anywhere — mutation callbacks, event handlers —
// without threading a context provider through the tree.

export type ToastVariant = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  message: string
  variant: ToastVariant
}

const DEFAULT_TTL_MS = 4000

let toasts: Toast[] = []
let nextId = 1
const listeners = new Set<() => void>()

function emit() {
  // New array identity so useSyncExternalStore detects the change.
  toasts = [...toasts]
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): Toast[] {
  return toasts
}

export function dismissToast(id: number) {
  toasts = toasts.filter((t) => t.id !== id)
  emit()
}

export function toast(message: string, variant: ToastVariant = 'info', ttlMs = DEFAULT_TTL_MS) {
  const id = nextId++
  toasts = [...toasts, { id, message, variant }]
  emit()
  if (ttlMs > 0) {
    setTimeout(() => dismissToast(id), ttlMs)
  }
  return id
}

/** Subscribe a component to the live toast list. */
export function useToasts(): Toast[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
